import {
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BaseProps, HTMLElementWithAnchor, Popover2025 } from './Popover2025';
import { StyledPopoverContainer } from './StyledPopoverContainer';

const ANCHOR_POLL_MS = 250;

export type PopoverTrigger = {
  popoverId: string;
  isOpen: boolean;
};

/** Spread onto the element that opens a `PopoverAtAnchor` with the same id. */
export const popoverTriggerAttributes = ({
  popoverId,
  isOpen,
}: PopoverTrigger) => ({
  'data-popover-trigger': popoverId,
  'aria-haspopup': 'dialog' as const,
  'aria-expanded': isOpen,
  'aria-controls': popoverId,
});

const findVisibleAnchor = (popoverId: string): HTMLElementWithAnchor | null =>
  Array.from(
    document.querySelectorAll<HTMLElement>(
      `[data-popover-trigger="${CSS.escape(popoverId)}"]`
    )
  ).find(anchor => anchor.checkVisibility()) ?? null;

const isAnchorFor = (
  popoverId: string,
  anchor: HTMLElementWithAnchor | null
): anchor is HTMLElementWithAnchor =>
  Boolean(anchor?.isConnected) && anchor?.dataset.popoverTrigger === popoverId;

const createAnchorRef = (
  popoverId: string
): RefObject<HTMLElementWithAnchor | null> => {
  let anchor: HTMLElementWithAnchor | null = null;
  return {
    get current() {
      if (!isAnchorFor(popoverId, anchor)) {
        anchor = findVisibleAnchor(popoverId);
      }
      return anchor;
    },
  };
};

const useAnchorRef = (
  popoverId: string,
  isOpen: boolean
): RefObject<HTMLElementWithAnchor | null> => {
  const [anchorVersion, setAnchorVersion] = useState(0);
  const anchorRef = useMemo(
    () => createAnchorRef(popoverId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new ref re-anchors to a replaced anchor element
    [popoverId, anchorVersion]
  );

  useEffect(() => {
    if (!isOpen) return;
    const anchor = anchorRef.current;
    const intervalId = setInterval(() => {
      if (anchorRef.current !== anchor) setAnchorVersion(prev => prev + 1);
    }, ANCHOR_POLL_MS);
    return () => clearInterval(intervalId);
  }, [anchorRef, isOpen]);

  return anchorRef;
};

type PopoverAtAnchorProps = Partial<BaseProps> & {
  id: string;
  isOpen: boolean;
  onClose: VoidFunction;
  onClickOutside?: VoidFunction;
  children: ReactNode;
};

/**
 * A popover anchored to an element rendered elsewhere on the page, matched by
 * `popoverTriggerAttributes` on that element. The open state is
 * controlled by the consumer. Prefer `ControlledPopover` when the anchor can
 * be wrapped.
 */
export const PopoverAtAnchor = ({
  id,
  isOpen,
  onClose,
  onClickOutside,
  children,
  placement = { side: 'bottom', overflow: 'center' },
  ...popoverProps
}: PopoverAtAnchorProps) => {
  const anchorRef = useAnchorRef(id, isOpen);

  return (
    <Popover2025
      id={id}
      isOpen={isOpen}
      onClose={onClose}
      onClickOutside={onClickOutside}
      anchorRef={anchorRef}
      placement={placement}
      {...popoverProps}
    >
      <StyledPopoverContainer
        isOpen={isOpen}
        preferredSide={placement.side}
        anchorId={id}
        anchorRef={anchorRef}
      >
        {children}
      </StyledPopoverContainer>
    </Popover2025>
  );
};
